import { Search, X } from "lucide-react";
import { useEffect, useRef, useState, type FormEvent } from "react";
import { useClient } from "../lib/client.ts";
import { suggest, type Suggestion } from "../lib/suggest.ts";

interface Props {
  query: string;
  onQuery: (q: string) => void;
  /** "bar": the compact box in the top bar (query shown as a pill); "large": YouTrack's full-width box above the issue list. */
  size?: "bar" | "large";
  className?: string;
}

/** Query input with the field / value autocomplete. Enter submits; Tab (or Enter after arrowing) completes. */
export function SearchBox({ query, onQuery, size = "bar", className = "" }: Props) {
  const large = size === "large";
  const [draft, setDraft] = useState(query);
  const [editing, setEditing] = useState(false);
  useEffect(() => setDraft(query), [query]);
  const schema = useClient((s) => s.schema);
  const me = useClient((s) => s.me);
  const input = useRef<HTMLInputElement>(null);
  const [caret, setCaret] = useState(0);
  const [sel, setSel] = useState(0);
  const [navigated, setNavigated] = useState(false);
  const [showSuggest, setShowSuggest] = useState(false);
  const sug = editing && showSuggest && schema && me ? suggest(schema, draft, caret, me.id) : null;
  const apply = (s: Suggestion): void => {
    if (!sug) return;
    const next = draft.slice(0, sug.start) + s.insert + draft.slice(sug.end);
    setDraft(next);
    setSel(0);
    requestAnimationFrame(() => {
      const el = input.current;
      if (!el) return;
      const pos = sug.start + s.insert.length;
      el.setSelectionRange(pos, pos);
      setCaret(pos);
    });
  };
  const submit = (e: FormEvent): void => {
    e.preventDefault();
    setEditing(false);
    onQuery(draft.trim());
  };

  return (
    <form
      onSubmit={submit}
      className={`relative flex items-center rounded-md border border-line bg-surface focus-within:border-focus ${large ? "h-10 pl-3" : "h-8 px-2"} ${className}`}
      role="search"
    >
      {!large && !editing && query && (
        <span className="mr-2 flex max-w-[12rem] items-center gap-1 rounded bg-accent-soft px-2 py-0.5 text-xs">
          <span className="truncate">{query}</span>
          <button type="button" className="text-muted hover:text-ink" title="Clear" onClick={() => onQuery("")}>
            <X size={12} />
          </button>
        </span>
      )}
      <input
        ref={input}
        className={`min-w-0 flex-1 bg-transparent outline-none ${large ? "text-[15px]" : "font-mono text-[13px]"}`}
        placeholder="Enter search request"
        value={editing ? draft : large ? query : ""}
        onFocus={() => {
          setEditing(true);
          setDraft(query);
          setShowSuggest(true);
        }}
        onBlur={() => setTimeout(() => setEditing(false), 150)}
        onChange={(e) => {
          setDraft(e.target.value);
          setCaret(e.target.selectionStart ?? e.target.value.length);
          setSel(0);
          setNavigated(false);
          setShowSuggest(true);
        }}
        onSelect={(e) => setCaret((e.target as HTMLInputElement).selectionStart ?? 0)}
        onKeyDown={(e) => {
          if (!sug || sug.items.length === 0) return;
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setNavigated(true);
            setSel((i) => (i + 1) % sug.items.length);
          } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setNavigated(true);
            setSel((i) => (i - 1 + sug.items.length) % sug.items.length);
          } else if (e.key === "Tab" || (e.key === "Enter" && navigated)) {
            // Tab completes the highlighted item; Enter does too after arrowing, otherwise it submits.
            e.preventDefault();
            setNavigated(false);
            apply(sug.items[sel]!);
          } else if (e.key === "Escape") setShowSuggest(false);
        }}
        aria-label="Query"
        autoComplete="off"
        spellCheck={false}
      />
      {sug && sug.items.length > 0 && (
        <ul className="absolute top-full left-0 z-30 mt-1 max-h-72 w-full overflow-y-auto rounded border border-line bg-surface py-1 shadow-lg" role="listbox">
          {sug.items.map((s, i) => (
            <li key={s.label}>
              <button
                type="button"
                className={`flex w-full items-center gap-3 px-3 py-1 text-left font-mono text-[13px] ${i === sel ? "bg-sunken" : "hover:bg-sunken"}`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  apply(s);
                }}
              >
                <span>{s.label}</span>
                {s.hint && <span className="ml-auto font-sans text-xs text-muted">{s.hint}</span>}
              </button>
            </li>
          ))}
        </ul>
      )}
      {large && query && !editing && (
        <button type="button" className="icon-btn h-7 w-7 text-muted" title="Clear" onClick={() => onQuery("")}>
          <X size={15} />
        </button>
      )}
      {large ? (
        <button type="submit" className="flex h-full w-11 shrink-0 items-center justify-center border-l border-line text-muted hover:text-ink" title="Search">
          <Search size={17} />
        </button>
      ) : (
        <button type="submit" className="icon-btn h-6 w-6" title="Search">
          <Search size={15} />
        </button>
      )}
    </form>
  );
}
